<script setup lang="ts">
import { computed, type PropType } from 'vue';
import GaiaAvatar from '../base/GaiaAvatar.vue';
import GaiaButton from '../base/GaiaButton.vue';
import GaiaDeleteButton from '../base/GaiaDeleteButton.vue';
import GaiaRiskPill from '../base/GaiaRiskPill.vue';
import GaiaStatusIndicator from '../base/GaiaStatusIndicator.vue';
import GaiaText from '../base/GaiaText.vue';
import { gaiaFontSize, gaiaNeutral, gaiaShadow, gaiaStatus, gaiaTracking } from '../themes/tokens';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  /** Owner initials for the avatar; `owner` feeds its tooltip title. */
  initials: {
    type: String,
    default: undefined,
  },
  owner: {
    type: String,
    default: undefined,
  },
  /** Agent state marker: running pulse or the settled check. */
  running: {
    type: Boolean,
    default: false,
  },
  settled: {
    type: Boolean,
    default: false,
  },
  /** Identified risk count — renders the tinted risk chip (0 = green). */
  riskCount: {
    type: Number,
    default: undefined,
  },
  /** Due label, e.g. "Due Aug 27"; `urgent` colors it red. */
  due: {
    type: String,
    default: undefined,
  },
  urgent: {
    type: Boolean,
    default: false,
  },
  /** Dimmed while being dragged. */
  dragging: {
    type: Boolean,
    default: false,
  },
  openLabel: {
    type: String,
    default: 'Review',
  },
  /**
   * Board density (the mock's Comfortable/Compact switch). Compact tightens the
   * card's own padding and gaps so more cards fit a column; nothing else about
   * the card changes. Set it on the column too, or the stack gap disagrees.
   */
  density: {
    type: String as PropType<'comfortable' | 'compact'>,
    default: 'comfortable',
  },
});

const emit = defineEmits<{ open: []; delete: [] }>();

const riskLevel = computed(() => {
  if (props.riskCount === undefined) return undefined;
  if (props.riskCount === 0) return 'none' as const;
  return props.riskCount >= 5 ? ('high' as const) : ('medium' as const);
});
const riskLabel = computed(() => {
  if (props.riskCount === 0) return 'No risks identified';
  return `${props.riskCount} risk${props.riskCount === 1 ? '' : 's'} identified`;
});

const border = gaiaNeutral.borderCard;
const borderHover = gaiaNeutral.borderHover;
const surface = gaiaNeutral.surface;
const dueColor = gaiaNeutral.textFaint;
const urgentColor = gaiaStatus.risk;
const footBorder = gaiaNeutral.borderSubtle;
const lift = gaiaShadow.card;
const liftHover = gaiaShadow.cardHover;
// The card's title clamps to two lines and the meta row is a flex strip, so
// both are selector-bound rules rather than GaiaText props.
const metaSize = gaiaFontSize.micro;
const snugTracking = gaiaTracking.snug;

// Density: two values per spacing decision, bound rather than classed so the
// gaps n-flex writes inline and the paddings CSS owns stay in step.
const compact = computed(() => props.density === 'compact');
const stackGap = computed(() => (compact.value ? 6 : 10));
const cardPad = computed(() => (compact.value ? '8px' : '12px'));
const footPad = computed(() => (compact.value ? '6px' : '10px'));
</script>

<template>
  <!-- Review card from the Reviews Kanban mock: agent state, title, owner
       avatar, risk chip, due date, footer action + delete. Drag handling
       (draggable/dragstart) is the board's concern via attrs fallthrough. -->
  <n-flex
    vertical
    :size="stackGap"
    :wrap="false"
    class="gaia-kanban-card"
    :class="{ dragging: props.dragging }"
  >
    <n-flex align="flex-start" :size="8" :wrap="false">
      <n-flex
        v-if="props.running"
        align="center"
        :size="0"
        :wrap="false"
        class="state"
        title="Agent running"
      >
        <gaia-status-indicator status="running" />
      </n-flex>
      <n-flex
        v-else-if="props.settled"
        align="center"
        :size="0"
        :wrap="false"
        class="state"
        title="Done — no agent running"
      >
        <gaia-status-indicator status="done" />
      </n-flex>
      <gaia-text class="title" size="row" weight="semibold" tone="ink">
        {{ props.title }}
      </gaia-text>
      <gaia-avatar
        v-if="props.initials"
        :size="24"
        :title="props.owner"
        :style="{ marginTop: '-3px' }"
      >
        {{ props.initials }}
      </gaia-avatar>
    </n-flex>
    <n-flex align="center" :size="6">
      <!-- Anything the board knows about the review that isn't a risk count —
           today the extraction progress, since the API has no risk field yet. -->
      <slot name="meta" />
      <gaia-risk-pill v-if="riskLevel" :level="riskLevel">{{ riskLabel }}</gaia-risk-pill>
      <n-flex
        v-if="props.due"
        inline
        align="center"
        :size="5"
        :wrap="false"
        class="due"
        :class="{ urgent: props.urgent }"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.9"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
        {{ props.due }}
      </n-flex>
    </n-flex>
    <n-flex
      align="center"
      :size="8"
      :wrap="false"
      :style="{ paddingTop: footPad, borderTop: `1px solid ${footBorder}` }"
    >
      <gaia-button size="small" @click="emit('open')">{{ props.openLabel }}</gaia-button>
      <slot name="actions" />
      <gaia-delete-button
        size="md"
        subject="review"
        :style="{ marginLeft: 'auto' }"
        @click="emit('delete')"
      />
    </n-flex>
  </n-flex>
</template>

<style scoped>
.gaia-kanban-card {
  background: v-bind(surface);
  border: 1px solid v-bind(border);
  border-radius: 10px;
  padding: v-bind(cardPad);
  cursor: grab;
  box-shadow: v-bind(lift);
}

.gaia-kanban-card:hover {
  border-color: v-bind(borderHover);
  box-shadow: v-bind(liftHover);
}

.gaia-kanban-card.dragging {
  opacity: 0.45;
}

.state {
  height: 18px;
  flex: none;
}

.title {
  flex: 1;
  min-width: 0;
  line-height: 18px;
  letter-spacing: v-bind(snugTracking);
  text-wrap: pretty;
}

.due {
  margin-left: auto;
  font-size: v-bind(metaSize);
  color: v-bind(dueColor);
  white-space: nowrap;
}

.due.urgent {
  color: v-bind(urgentColor);
}
</style>
